/**
 * Almanac — the traveler's two-page paper spread, opened off the day-wheel.
 *
 * Doctrine: docs/design/ui-c5-indigo-ink.md (Layer 3, "day-wheel Almanac").
 * The day-wheel is the instrument that keeps watch; the almanac is the paper
 * the traveler keeps beside it. Left page: one hanko stamp per day lived and
 * the moon drawn in ink. Right page: today's marks, pinned, plus a few small
 * annotations in the margin.
 *
 * All layout/selection/naming maths lives in the Phaser-free `almanacModel`
 * module (tested); this class owns DOM, styles and lifecycle only.
 *
 * Input contract: while closed the almanac swallows nothing — its root is
 * `display:none` and the only key it inspects is its own toggle. While open it
 * takes Escape (capture phase, stopped) so the world never sees it.
 */

import type * as Phaser from 'phaser';
import almanacCss from './almanac.css?inline';
import { getString } from '../locale';
import { loadStats } from '../PersistentStats';
import { formatPlaytime, loadAllSlots } from '../SaveSlotPersistence';
import {
  emitAlmanacClosed,
  emitAlmanacOpened,
  onAlmanacToggle,
  onDayChanged,
  onSlipShow,
  onTimeTick,
  readJournalEntries,
  type HudUnsub,
} from '../../game/HudState';
import { readDayCount } from './dayCount';
import {
  buildAnnotations,
  buildStampGrid,
  moonDiagram,
  moonLocaleKey,
  selectMarks,
  type MarkSource,
} from './almanacModel';

const STYLE_ELEMENT_ID = 'c5-almanac-styles';

/** Fade-out dwell before the spread leaves the layout. Matches --c5-dur. */
const CLOSE_MS = 240;

/** Session whispers kept for the right page; `selectMarks` caps what shows. */
const MAX_SESSION_SLIPS = 12;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = almanacCss;
  document.head.appendChild(style);
}

/** True when a text field owns the keyboard (hearth chat, name entry, …). */
function typingInAField(): boolean {
  const node = document.activeElement as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || node.isContentEditable === true;
}

export class Almanac {
  private readonly root: HTMLDivElement;
  private readonly spread: HTMLDivElement;
  private readonly stampGrid: HTMLDivElement;
  private readonly earlier: HTMLParagraphElement;
  private readonly moonDisc: HTMLDivElement;
  private readonly moonDark: HTMLElement;
  private readonly moonTerm: HTMLElement;
  private readonly moonName: HTMLSpanElement;
  private readonly moonLit: HTMLSpanElement;
  private readonly marks: HTMLUListElement;
  private readonly marksEmpty: HTMLParagraphElement;
  private readonly notes: HTMLDivElement;
  private readonly closeBtn: HTMLButtonElement;

  private game: Phaser.Game | null = null;
  private readonly unsubs: HudUnsub[] = [];
  private readonly onKeyDown: (ev: KeyboardEvent) => void;
  private trigger: HTMLElement | null = null;
  private triggerHandlers: Array<[string, EventListener]> = [];

  private open = false;
  private keyboardWasEnabled = true;
  private closeTimer: number | null = null;
  private day = 1;
  private moonPhase = 0;
  private sessionSlips: MarkSource[] = [];
  private nextSlipId = 1;

  constructor(parent: HTMLElement) {
    injectStyles();

    this.root = el('div', 'c5-almanac');
    this.root.hidden = true;

    const scrim = el('div', 'c5-almanac-scrim');
    scrim.addEventListener('click', () => this.close());

    this.spread = el('div', 'c5-almanac-spread');
    this.spread.setAttribute('role', 'dialog');
    this.spread.setAttribute('aria-modal', 'true');
    this.spread.setAttribute('aria-label', getString('c5.a11y.almanac'));

    // ── left page: the days, then the moon ──────────────────────────────
    const left = el('div', 'c5-almanac-page c5-almanac-left');
    left.appendChild(pageTitle(getString('c5.almanac.days_title')));

    this.stampGrid = el('div', 'c5-almanac-stamps');
    left.appendChild(this.stampGrid);

    this.earlier = el('p', 'c5-almanac-earlier');
    this.earlier.hidden = true;
    left.appendChild(this.earlier);

    const moonBlock = el('div', 'c5-almanac-moon');
    this.moonDisc = el('div', 'c5-moon-disc');
    this.moonDark = el('i', 'c5-moon-dark');
    this.moonTerm = el('i', 'c5-moon-term');
    this.moonDisc.append(this.moonDark, this.moonTerm);
    const moonText = el('div', 'c5-moon-text');
    const moonHeading = el('em');
    moonHeading.textContent = getString('c5.almanac.moon_title');
    this.moonName = el('span', 'c5-moon-name');
    this.moonLit = el('span', 'c5-moon-lit');
    moonText.append(moonHeading, this.moonName, this.moonLit);
    moonBlock.append(this.moonDisc, moonText);
    left.appendChild(moonBlock);

    // ── right page: today's marks, then the margin notes ────────────────
    const right = el('div', 'c5-almanac-page c5-almanac-right');
    right.appendChild(pageTitle(getString('c5.almanac.marks_title')));

    this.marks = el('ul', 'c5-almanac-marks');
    right.appendChild(this.marks);

    this.marksEmpty = el('p', 'c5-almanac-empty');
    this.marksEmpty.textContent = getString('c5.almanac.marks_empty');
    this.marksEmpty.hidden = true;
    right.appendChild(this.marksEmpty);

    this.notes = el('div', 'c5-almanac-notes');
    right.appendChild(this.notes);

    this.closeBtn = el('button', 'c5-almanac-close');
    this.closeBtn.type = 'button';
    this.closeBtn.textContent = getString('c5.almanac.close');
    this.closeBtn.addEventListener('click', () => this.close());

    this.spread.append(left, el('div', 'c5-almanac-seam'), right, this.closeBtn);
    this.root.append(scrim, this.spread);
    parent.appendChild(this.root);

    this.day = readDayCount();

    // Capture phase so Escape never reaches Phaser's keyboard plugin while
    // the spread is open; the toggle key is the only thing read while closed.
    this.onKeyDown = (ev: KeyboardEvent) => this.handleKey(ev);
    window.addEventListener('keydown', this.onKeyDown, true);
  }

  /** Make an element (the day-wheel) open the almanac. */
  bindTrigger(node: HTMLElement): void {
    this.trigger = node;
    node.classList.add('c5-almanac-trigger');
    node.tabIndex = 0;
    node.setAttribute('role', 'button');
    const key = getString('c5.almanac.key');
    node.setAttribute(
      'aria-label',
      getString('c5.a11y.day_wheel_button').replace('{0}', key),
    );
    node.title = `${getString('c5.almanac.hint')} (${key})`;

    const onClick: EventListener = () => this.toggle();
    const onKey: EventListener = (ev) => {
      const k = (ev as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') {
        ev.preventDefault();
        this.toggle();
      }
    };
    node.addEventListener('click', onClick);
    node.addEventListener('keydown', onKey);
    this.triggerHandlers = [
      ['click', onClick],
      ['keydown', onKey],
    ];
  }

  /** Subscribe to the HUD bus. Call once, after boot. */
  attach(game: Phaser.Game): void {
    this.game = game;
    this.unsubs.push(
      onDayChanged(game, (d) => {
        this.day = Math.max(1, Math.floor(d.day));
        if (this.open) this.renderDays();
      }),
      onTimeTick(game, (t) => {
        this.moonPhase = t.moonPhase;
        if (this.open) this.renderMoon();
      }),
      onSlipShow(game, (p) => this.recordSlip(p.text)),
      onAlmanacToggle(game, () => this.toggle()),
    );
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.day = readDayCount();
    this.render();
    this.root.hidden = false;
    // Next frame so the unfold transition has a start value to animate from.
    requestAnimationFrame(() => this.root.classList.add('c5-almanac-shown'));
    this.closeBtn.focus({ preventScroll: true });
    // Reading never walks the traveler — same guard LetterboxUi uses.
    const keyboard = this.game?.input?.keyboard;
    if (keyboard) {
      this.keyboardWasEnabled = keyboard.enabled;
      keyboard.enabled = false;
    }
    if (this.game) emitAlmanacOpened(this.game);
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    const keyboard = this.game?.input?.keyboard;
    if (keyboard) keyboard.enabled = this.keyboardWasEnabled;
    this.root.classList.remove('c5-almanac-shown');
    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;
      this.root.hidden = true;
    }, CLOSE_MS);
    this.trigger?.focus({ preventScroll: true });
    if (this.game) emitAlmanacClosed(this.game);
  }

  destroy(): void {
    for (const off of this.unsubs) off();
    this.unsubs.length = 0;
    window.removeEventListener('keydown', this.onKeyDown, true);
    if (this.trigger) {
      for (const [type, handler] of this.triggerHandlers) {
        this.trigger.removeEventListener(type, handler);
      }
      this.trigger = null;
      this.triggerHandlers = [];
    }
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.root.remove();
    this.game = null;
  }

  // ── input ─────────────────────────────────────────────────────────────

  private handleKey(ev: KeyboardEvent): void {
    if (typingInAField()) return;
    if (this.open && ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      this.close();
      return;
    }
    if (ev.ctrlKey || ev.altKey || ev.metaKey) return;
    const toggleKey = getString('c5.almanac.key').toLowerCase();
    if (ev.key.toLowerCase() !== toggleKey) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    this.toggle();
  }

  // ── data ──────────────────────────────────────────────────────────────

  /** Whispers drift past as slips; the almanac remembers today's. */
  private recordSlip(text: string): void {
    const body = text.trim();
    if (body.length === 0) return;
    this.sessionSlips.push({
      id: `slip-${this.nextSlipId++}`,
      heading: getString('c5.almanac.mark.whisper'),
      body,
      timestamp: Date.now(),
    });
    if (this.sessionSlips.length > MAX_SESSION_SLIPS) {
      this.sessionSlips = this.sessionSlips.slice(-MAX_SESSION_SLIPS);
    }
    if (this.open) this.renderMarks();
  }

  private collectMarks(): MarkSource[] {
    const entries = this.game ? readJournalEntries(this.game) : [];
    return [...entries, ...this.sessionSlips];
  }

  // ── render ────────────────────────────────────────────────────────────

  private render(): void {
    this.renderDays();
    this.renderMoon();
    this.renderMarks();
    this.renderNotes();
  }

  private renderDays(): void {
    const grid = buildStampGrid(this.day);
    this.stampGrid.style.gridTemplateColumns = `repeat(${grid.columns}, 1fr)`;
    const label = getString('c5.almanac.day_stamp_label');

    const stamps = grid.cells.map((cell) => {
      const stamp = el('i', cell.fresh ? 'c5-stamp c5-stamp-fresh' : 'c5-stamp');
      stamp.textContent = String(cell.day);
      stamp.setAttribute('aria-label', label.replace('{0}', String(cell.day)));
      // A hand-stamped page is never perfectly square.
      const lean = ((cell.day * 37) % 9) - 4;
      stamp.style.transform = `rotate(${lean * 0.4}deg)`;
      return stamp;
    });
    this.stampGrid.replaceChildren(...stamps);

    if (grid.earlierDays > 0) {
      this.earlier.textContent = getString('c5.almanac.earlier_days').replace(
        '{0}',
        String(grid.earlierDays),
      );
      this.earlier.hidden = false;
    } else {
      this.earlier.hidden = true;
    }
  }

  private renderMoon(): void {
    const moon = moonDiagram(this.moonPhase);
    this.moonDark.style.left = moon.waxing ? '0' : '50%';
    this.moonTerm.style.width = `${(moon.terminatorRatio * 100).toFixed(1)}%`;
    this.moonTerm.classList.toggle('c5-moon-term-ink', moon.terminatorInk);
    this.moonName.textContent = getString(moonLocaleKey(moon.name));
    this.moonLit.textContent = getString('c5.almanac.moon.lit').replace(
      '{0}',
      String(Math.round(moon.illumination * 100)),
    );
    this.moonDisc.setAttribute('aria-hidden', 'true');
  }

  private renderMarks(): void {
    const marks = selectMarks(this.collectMarks());
    const items = marks.map((mark, i) => {
      const li = el('li', 'c5-almanac-slip');
      li.style.transform = `rotate(${(i % 2 === 0 ? -1 : 1) * 0.5}deg)`;
      if (mark.heading.length > 0) {
        const b = el('b');
        b.textContent = mark.heading;
        li.appendChild(b);
      }
      if (mark.body.length > 0) {
        const span = el('span');
        span.textContent = mark.body;
        li.appendChild(span);
      }
      return li;
    });
    this.marks.replaceChildren(...items);
    this.marksEmpty.hidden = items.length > 0;
  }

  private renderNotes(): void {
    const stats = loadStats();
    let seconds = 0;
    for (const slot of loadAllSlots()) seconds += slot?.playtimeSeconds ?? 0;

    const notes = buildAnnotations({
      runs: stats.totalRunsCompleted,
      unburdened: stats.totalEnemiesKilled,
      gathered: stats.totalLootCollected,
      deepestFloor: stats.highestFloorReached,
      playtimeLabel: seconds > 0 ? formatPlaytime(seconds) : '',
    });

    this.notes.replaceChildren(
      ...notes.map((note) => {
        const row = el('span', 'c5-almanac-note');
        const value = el('b');
        value.textContent = note.value;
        const label = el('em');
        label.textContent = getString(note.labelKey);
        row.append(value, label);
        return row;
      }),
    );
    this.notes.hidden = notes.length === 0;
  }
}

function pageTitle(text: string): HTMLHeadingElement {
  const h = document.createElement('h2');
  h.className = 'c5-almanac-title';
  h.textContent = text;
  return h;
}
